import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import { useNavigate } from 'react-router-dom';
import colors from '../../assets/theme/colors';
import {
  getKnowledgeItemTypeLabel,
  isFeedingKnowledgeItem,
  subscribeToKnowledgeItems,
} from './knowledgeItemReadService';
import {
  setKnowledgeItemFactStatus,
} from './knowledgeItemService';
import {
  fetchCareSourceById,
} from '../careSourceProcessing/careSourceActivityService';
import {
  getCareSourceOriginalText,
  getCareSourceTitle,
} from '../careSourceProcessing/careSourceStatusModel';
import {
  getCareDocumentPurposeLabel,
  getCareDocumentResolvedGroups,
} from '../careSourceProcessing/careDocumentPresentation';
import { useCareSourceHistory } from '../careSourceProcessing/useCareSourceHistory';
import { buildProfileSourceViewerModel } from '../profileContract/profileSourceViewer';
import { CARE_PROFILE_ROUTE } from '../../constants/profileRoutes';
import { groupKnowledgeItemsByProfileChapter } from './careKnowledgeProfileGrouping';

const formatCapturedAt = (value) => {
  if (!value) return '';
  const date = typeof value.toDate === 'function'
    ? value.toDate()
    : value instanceof Date
      ? value
      : new Date(value);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const getPrimarySourceRef = (item = {}) => (
  Array.isArray(item.sourceRefs) && item.sourceRefs.length ? item.sourceRefs.find(ref=>ref.supportStatus!=='withdrawn') || item.sourceRefs[0] : null
);

const getSourceRefs = (item = {}) => {
  const refs = Array.isArray(item.sourceRefs) ? item.sourceRefs.filter(Boolean) : [];
  if (refs.length) return refs;
  return item.sourceText ? [{ sourceText: item.sourceText }] : [];
};

const isTimelineSourceRef = (sourceRef = {}, item = {}) => (
  sourceRef.sourceCollection !== 'careEvidenceReviewHistory' && (sourceRef.sourceCollection === 'dailyLogs'
  || sourceRef.sourceContext === 'timeline'
  || Boolean(sourceRef.timelineEntryId)
  || item.capturedBy?.sourceContext === 'timeline')
);

const buildSourceRefLabel = (sourceRef = {}, item = {}) => {
  if (!sourceRef) {
    return item.sourceText ? 'from source' : '';
  }

  if (isTimelineSourceRef(sourceRef, item)) {
    const dateLabel = sourceRef.timelineDateLabel
      || (sourceRef.timelineDate ? formatCapturedAt(sourceRef.timelineDate) : '')
      || formatCapturedAt(item.capturedAt);
    return dateLabel ? `from ${dateLabel.replace(/^Timeline date /i, 'Timeline, ')}` : 'from Timeline';
  }

  const title = sourceRef.sourceTitle || sourceRef.sourceType || 'source';
  return `from ${title}`;
};

const buildSourceLabel = (item = {}) => buildSourceRefLabel(getPrimarySourceRef(item), item);

const buildSourceGroupKey = (item = {}) => {
  const sourceRef = getPrimarySourceRef(item);
  if (sourceRef?.sourceId) return `source:${sourceRef.sourceId}`;
  if (sourceRef?.timelineEntryId) return `timeline:${sourceRef.timelineEntryId}`;
  if (item.captureId) return `capture:${item.captureId}`;
  const sourceLabel = buildSourceLabel(item);
  return sourceLabel ? `label:${sourceLabel}` : 'manual:saved-care-notes';
};

const buildSavedDateLabel = (item = {}) => {
  const sourceRef = getPrimarySourceRef(item);
  return formatCapturedAt(sourceRef?.createdAt)
    || formatCapturedAt(sourceRef?.capturedAt)
    || formatCapturedAt(item.capturedAt);
};

const buildSourceGroupTitle = () => 'Long-term care notes';

const buildSourceGroupContext = (item = {}) => {
  const savedDateLabel = buildSavedDateLabel(item);
  return savedDateLabel ? `Saved ${savedDateLabel}` : 'Saved update';
};

const getSourceAudit = (source = null, items = []) => {
  const extraction = source?.extractionResult || {};
  const timeline = Array.isArray(extraction.suggestedRecords)
    ? extraction.suggestedRecords
    : Array.isArray(source?.suggestedRecords) ? source.suggestedRecords : [];
  const details = [
    ...(Array.isArray(source?.suggestedCareKnowledgeItems) ? source.suggestedCareKnowledgeItems : []),
    ...timeline.flatMap((entry) => Array.isArray(entry?.careDetails) ? entry.careDetails : []),
    ...items,
  ];
  const document = getCareDocumentResolvedGroups(source).map((group) => ({
    id: group.id,
    title: group.title,
    bucketLabel: group.bucketLabel,
    items: group.items,
  }));
  const review = [
    ...(Array.isArray(extraction.suggestedMedicationUpdates) ? extraction.suggestedMedicationUpdates : []),
    ...(Array.isArray(extraction.suggestedAllergyUpdates) ? extraction.suggestedAllergyUpdates : []),
    ...(Array.isArray(extraction.suggestedGoalUpdates) ? extraction.suggestedGoalUpdates : []),
    ...(Array.isArray(extraction.reviewItems) ? extraction.reviewItems : []),
    ...(Array.isArray(source?.suggestedMedicationUpdates) ? source.suggestedMedicationUpdates : []),
    ...(Array.isArray(source?.suggestedAllergyUpdates) ? source.suggestedAllergyUpdates : []),
    ...(Array.isArray(source?.suggestedGoalUpdates) ? source.suggestedGoalUpdates : []),
    ...(Array.isArray(source?.reviewItems) ? source.reviewItems : []),
  ];
  const profile = source?.suggestedProfileUpdates
    || source?.profileUpdates
    || extraction.suggestedProfileUpdates
    || extraction.profileUpdates
    || {};
  const profileItems = Array.isArray(profile)
    ? profile
    : Object.entries(profile).flatMap(([label, value]) => (
      value && typeof value === 'object' && !Array.isArray(value)
        ? [{ ...value, title: value.title || label }]
        : value ? [{ title: label, content: String(value) }] : []
    ));
  return {
    timeline,
    details: [...new Map(details.map((item, index) => [item.id || item.clientId || `${item.title}-${index}`, item])).values()],
    review,
    document,
    profile: profileItems,
  };
};

const buildSourceGroupSubtitle = (items = [], source = null) => {
  const audit = getSourceAudit(source, items);
  const count = audit.timeline.length + audit.details.length + audit.review.length + audit.document.length + audit.profile.length;
  return count ? `${count} result${count === 1 ? '' : 's'} from this source` : 'No caregiver-facing details extracted yet';
};

const getSourceRefSourceId = (item = {}) => String(getPrimarySourceRef(item)?.sourceId || '').trim();

const normalizeOriginalText = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();

const getSourceOriginalTexts = (items = [], source = null) => {
  const texts = [];
  const fullSourceText = source ? getCareSourceOriginalText(source) : '';
  if (fullSourceText) texts.push(fullSourceText);

  items.forEach((item) => {
    const sourceRef = getPrimarySourceRef(item);
    [
      sourceRef?.sourceText,
      item.sourceText,
    ].filter(Boolean).forEach((text) => texts.push(text));
  });

  const seen = new Set();
  return texts.filter((text) => {
    const normalized = normalizeOriginalText(text);
    if (!normalized || seen.has(normalized)) return false;
    seen.add(normalized);
    return true;
  });
};

const buildSourceKindLabel = (item = {}, source = null) => {
  const sourceRef = getPrimarySourceRef(item);
  const sourceType = String(
    source?.sourceType
    || source?.sourceMetadata?.sourceType
    || sourceRef?.sourceType
    || ''
  ).toLowerCase();
  const fetchedSourceTitle = source ? getCareSourceTitle(source) : '';
  const sourceTitle = (fetchedSourceTitle && fetchedSourceTitle !== 'Saved source')
    ? fetchedSourceTitle
    : sourceRef?.sourceTitle || '';
  const timelineSource = sourceRef ? isTimelineSourceRef(sourceRef, item) : false;

  if (timelineSource) return 'Timeline note';
  if (sourceTitle && sourceTitle !== 'Saved source') return sourceTitle;
  if (sourceType.includes('email')) return 'Email pasted';
  if (sourceType.includes('file') || sourceType.includes('image') || sourceType.includes('document')) return 'Document uploaded';
  if (sourceType.includes('typed') || sourceType.includes('text') || sourceType.includes('note')) return 'Care note pasted';
  if (!sourceRef && item.sourceText) return 'Care note pasted';
  return 'Care note added';
};

const buildSourcePanelTitle = (item = {}, source = null) => {
  const dateLabel = buildSavedDateLabel(item);
  const kindLabel = buildSourceKindLabel(item, source);
  return dateLabel ? `${kindLabel} · ${dateLabel}` : kindLabel;
};

const groupKnowledgeItemsBySource = (items = []) => {
  const groupsByKey = new Map();

  items.forEach((item) => {
    const key = buildSourceGroupKey(item);
    const existingGroup = groupsByKey.get(key);
    if (existingGroup) {
      existingGroup.items.push(item);
      return;
    }

    groupsByKey.set(key, {
      id: key,
      title: buildSourceGroupTitle(item),
      context: buildSourceGroupContext(item),
      items: [item],
    });
  });

  return Array.from(groupsByKey.values()).map((group) => ({
    ...group,
    subtitle: buildSourceGroupSubtitle(group.items, group.source),
  }));
};

const groupKnowledgeItemsWithSources = (items = [], sources = []) => {
  const groups = groupKnowledgeItemsBySource(items);
  const groupBySourceId = new Map();

  groups.forEach((group) => {
    const sourceId = getSourceRefSourceId(group.items[0])
      || String(group.items[0]?.captureId || '').trim();
    if (sourceId) groupBySourceId.set(sourceId, group);
  });

  sources.forEach((source) => {
    const sourceId = String(source.id || '').trim();
    const existing = groupBySourceId.get(sourceId);
    if (existing) {
      existing.source = source;
      existing.subtitle = buildSourceGroupSubtitle(existing.items, source);
      return;
    }
    groups.push({
      id: `source:${sourceId}`,
      title: buildSourceGroupTitle(),
      context: buildSourceGroupContext(source),
      items: [],
      source,
      subtitle: buildSourceGroupSubtitle([], source),
    });
  });

  return groups;
};

const requestCaptureReview = (item = {}) => {
  if (!item.captureId || typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
    return;
  }

  window.dispatchEvent(new CustomEvent('captureez:capture-review-request', {
    detail: {
      captureId: item.captureId,
      childId: item.childId || null,
    },
  }));
};

const KnowledgeItemSourceSheet = ({
  item,
  open,
  onClose,
}) => {
  const sourceRefs = getSourceRefs(item);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: { xs: '24px 24px 0 0', sm: '24px' },
          m: { xs: 0, sm: 2 },
          alignSelf: { xs: 'flex-end', sm: 'center' },
          width: { xs: '100%', sm: 'calc(100% - 32px)' },
        },
      }}
    >
      <DialogContent sx={{ p: { xs: 2.2, sm: 3 } }}>
        <Stack spacing={1.6}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Box>
              <Typography component="h2" sx={{ fontSize: { xs: '1.35rem', sm: '1.55rem' }, fontWeight: 950, color: colors.semantic.ink, lineHeight: 1.08 }}>
                Evidence
              </Typography>
              <Typography sx={{ mt: 0.65, fontSize: '0.9rem', color: alpha(colors.semantic.ink, 0.66), lineHeight: 1.45 }}>
                {`${sourceRefs.filter(ref=>ref.supportStatus!=='withdrawn').length} current supporting sources. Previous evidence is retained below.`}
              </Typography>
            </Box>
            <IconButton aria-label="Close" onClick={onClose}>
              <CloseRoundedIcon />
            </IconButton>
          </Stack>

          <Stack spacing={0.9}>
            {sourceRefs.map((sourceRef, index) => {
              const sourceText = sourceRef.sourceText || (index === 0 ? item.sourceText : '') || '';
              const sourceLabel = buildSourceRefLabel(sourceRef, item);
              const dateLabel = formatCapturedAt(sourceRef.confirmedAt || sourceRef.createdAt || sourceRef.capturedAt || sourceRef.timelineDate);
              return (
                <Box
                  key={`${sourceRef.timelineEntryId || sourceRef.sourceId || 'source'}-${index}`}
                  sx={{ borderRadius: '16px', border: `1px solid ${alpha(colors.semantic.line, 0.9)}`, p: 1.15 }}
                >
                  <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: colors.semantic.ink, lineHeight: 1.25 }}>
                    {sourceLabel.replace(/^from\s+/i, '') || 'Supporting source'}
                  </Typography>
                  {sourceRef.supportStatus==='withdrawn' ? <Chip size="small" label="Previous source — support withdrawn" /> : null}
                  {dateLabel ? (
                    <Typography sx={{ mt: 0.25, fontSize: '0.75rem', color: alpha(colors.semantic.ink, 0.58) }}>
                      {dateLabel}
                    </Typography>
                  ) : null}
                  <Typography sx={{ mt: 0.6, fontSize: '0.84rem', color: alpha(colors.semantic.ink, 0.68), lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>
                    {sourceText || 'The supporting source is linked to this care detail.'}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

const KnowledgeItemCard = ({
  item,
  showSourceButton = true,
  fixing = false,
  removing = false,
  onRemove = null,
}) => {
  const navigate = useNavigate();
  const [sourceSheetOpen, setSourceSheetOpen] = useState(false);
  const [restoringCurrent, setRestoringCurrent] = useState(false);
  const [visibilityError, setVisibilityError] = useState('');
  const evidenceCount = getSourceRefs(item).filter(ref=>ref.supportStatus!=='withdrawn').length;
  const capturedAtLabel = formatCapturedAt(item.capturedAt);
  const typeLabel = isFeedingKnowledgeItem(item)
    ? getKnowledgeItemTypeLabel('feeding')
    : getKnowledgeItemTypeLabel(item.knowledgeType);
  const sourceRef = getPrimarySourceRef(item);
  const timelineSource = sourceRef ? isTimelineSourceRef(sourceRef, item) : false;
  const canRestoreCurrent = item.factStatus === 'outdated'
    && item.knowledgeType !== 'medication'
    && item.knowledgeType !== 'goal'
    && item.profileChapterId !== 'goals';

  const handleRestoreCurrent = async () => {
    if (!item.id || restoringCurrent) return;
    setRestoringCurrent(true);
    setVisibilityError('');
    try {
      await setKnowledgeItemFactStatus(item.id, 'current');
    } catch (error) {
      setVisibilityError(error?.message || "We couldn't mark this as current right now.");
    } finally {
      setRestoringCurrent(false);
    }
  };

  return (
    <Box
      sx={{
        p: 1,
        borderRadius: '12px',
        border: `1px solid ${alpha(colors.brand.tint, 0.14)}`,
        bgcolor: '#FFFFFF',
      }}
    >
      <Stack spacing={0.65}>
        <Stack
          direction="row"
          spacing={0.75}
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.96rem', fontWeight: 500, color: colors.semantic.ink, lineHeight: 1.25 }}>
              {item.title}
            </Typography>
            <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 0.35, flexWrap: 'wrap', rowGap: 0.5 }}>
              {capturedAtLabel ? (
                <Typography sx={{ fontSize: '0.74rem', color: alpha(colors.semantic.ink, 0.58) }}>
                  {capturedAtLabel}
                </Typography>
              ) : null}
              {item.evidenceReview?.status === 'needs_review' ? <Chip size="small" label="Source changed — review needed" color="warning" /> : null}
              {item.needsReview ? (
                <Chip
                  size="small"
                  label="Needs review"
                  color="warning"
                  sx={{ height: 22, fontSize: '0.7rem', fontWeight: 800 }}
                />
              ) : null}
            </Stack>
          </Box>
          {fixing ? (
            <Button
              size="small"
              color="error"
              onClick={() => onRemove?.(item)}
              disabled={removing}
              sx={{
                flexShrink: 0,
                alignSelf: 'flex-start',
                minWidth: 0,
                minHeight: 24,
                px: 0,
                py: 0,
                fontSize: '0.76rem',
                fontWeight: 500,
                textTransform: 'none',
                borderRadius: '999px',
              }}
            >
              {removing ? 'Removing...' : 'Remove'}
            </Button>
          ) : canRestoreCurrent ? (
            <Button
              size="small"
              onClick={handleRestoreCurrent}
              disabled={restoringCurrent}
              sx={{
                alignSelf: { xs: 'flex-start', sm: 'center' },
                color: colors.brand.deep,
                fontWeight: 500,
                textTransform: 'none',
                borderRadius: '999px',
              }}
            >
              {restoringCurrent ? 'Restoring...' : 'Mark current'}
            </Button>
          ) : item.needsReview && item.captureId ? (
            <Button
              size="small"
              onClick={() => requestCaptureReview(item)}
              sx={{
                alignSelf: { xs: 'flex-start', sm: 'center' },
                color: colors.brand.deep,
                fontWeight: 500,
                textTransform: 'none',
                borderRadius: '999px',
              }}
            >
              Review
            </Button>
          ) : null}
        </Stack>

        <Typography sx={{ fontSize: '0.86rem', lineHeight: 1.45, color: alpha(colors.semantic.ink, 0.72), whiteSpace: 'pre-wrap' }}>
          {item.summary}
        </Typography>

        {item.profileRepresentation?.label ? (
          <Button
            size="small"
            onClick={() => navigate(CARE_PROFILE_ROUTE, {
              state: {
                childId: item.childId,
                profileChapterViewId: item.profileRepresentation.profileViewId,
                knowledgeFactId: item.id,
              },
            })}
            sx={{ alignSelf: 'flex-start', px: 0, textTransform: 'none', fontWeight: 500 }}
          >
            In Profile: {item.profileRepresentation.label} →
          </Button>
        ) : null}

        {visibilityError ? <Alert severity="warning">{visibilityError}</Alert> : null}

        <Stack direction="row" spacing={0.55} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
            <Chip
              size="small"
              label={typeLabel}
              sx={{
                height: 24,
                fontSize: '0.72rem',
                fontWeight: 900,
                color: colors.brand.deep,
                bgcolor: alpha(colors.brand.tint, 0.14),
              }}
            />
            {showSourceButton && evidenceCount ? (
              <Button
                size="small"
                variant="text"
                startIcon={timelineSource ? <TimelineOutlinedIcon sx={{ fontSize: 14 }} /> : <Inventory2OutlinedIcon sx={{ fontSize: 14 }} />}
                onClick={() => setSourceSheetOpen(true)}
                sx={{
                  minHeight: 24,
                  px: 0.75,
                  py: 0,
                  borderRadius: '999px',
                  fontSize: '0.76rem',
                  fontWeight: 900,
                  textTransform: 'none',
                  color: colors.brand.deep,
                  bgcolor: alpha(colors.brand.tint, 0.12),
                  '& .MuiButton-startIcon': { mr: 0.35 },
                  '&:hover': { bgcolor: alpha(colors.brand.tint, 0.22) },
                }}
              >
                {`Evidence (${evidenceCount})`}
              </Button>
            ) : null}
        </Stack>
      </Stack>
      <KnowledgeItemSourceSheet
        item={item}
        open={sourceSheetOpen}
        onClose={() => setSourceSheetOpen(false)}
      />
    </Box>
  );
};

const KnowledgeSourceGroupCard = ({ group }) => {
  const [expanded, setExpanded] = useState(false);
  const [originalOpen, setOriginalOpen] = useState(false);
  const [source, setSource] = useState(group.source || null);
  const [sourceLoading, setSourceLoading] = useState(false);
  const [sourceError, setSourceError] = useState('');
  const firstItem = group.items[0] || group.source || {};
  const sourceId = getSourceRefSourceId(firstItem);
  const originalTexts = useMemo(() => getSourceOriginalTexts(group.items, source), [group.items, source]);
  const panelTitle = buildSourcePanelTitle(firstItem, source);
  const sourceModel = useMemo(() => buildProfileSourceViewerModel({ source }), [source]);
  const audit = useMemo(() => getSourceAudit(source, group.items), [source, group.items]);

  useEffect(() => {
    let active = true;

    if (!originalOpen || !sourceId || source || sourceLoading) {
      return () => {
        active = false;
      };
    }

    setSourceLoading(true);
    setSourceError('');
    fetchCareSourceById(sourceId)
      .then((nextSource) => {
        if (!active) return;
        setSource(nextSource);
      })
      .catch((error) => {
        if (!active) return;
        setSourceError(error?.message || "We couldn't load the full original source.");
      })
      .finally(() => {
        if (active) {
          setSourceLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [originalOpen, source, sourceId, sourceLoading]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.1,
        borderRadius: '18px',
        border: `1px solid ${alpha(colors.brand.tint, 0.18)}`,
        bgcolor: '#FFFFFF',
        boxShadow: `0 10px 24px ${alpha(colors.semantic.ink, 0.05)}`,
      }}
    >
      <Stack spacing={0.95}>
        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ mb: 0.35, fontSize: '0.78rem', fontWeight: 500, color: colors.brand.deep, letterSpacing: 0.6, textTransform: 'uppercase' }}>
              {group.context}
            </Typography>
            <Typography sx={{ fontSize: '1.02rem', fontWeight: 500, color: colors.semantic.ink, lineHeight: 1.18 }}>
              {panelTitle}
            </Typography>
            <Typography sx={{ mt: 0.25, fontSize: '0.8rem', color: alpha(colors.semantic.ink, 0.62), lineHeight: 1.35 }}>
              {group.subtitle}
            </Typography>
            {source && getCareDocumentPurposeLabel(source) ? (
              <Typography sx={{ mt: 0.3, fontSize: '0.78rem', fontWeight: 700, color: alpha(colors.semantic.sageDeep, 0.9), lineHeight: 1.3 }}>
                Purpose: {getCareDocumentPurposeLabel(source)}
              </Typography>
            ) : null}
          </Box>

          <IconButton
            aria-label={expanded ? 'Collapse source' : 'Expand source'}
            onClick={() => setExpanded((current) => !current)}
            sx={{
              flexShrink: 0,
              color: colors.brand.deep,
              width: 32,
              height: 32,
              bgcolor: alpha(colors.brand.tint, 0.1),
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 160ms ease, background-color 160ms ease',
              '&:hover': { bgcolor: alpha(colors.brand.tint, 0.18) },
            }}
          >
            <ExpandMoreRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Stack spacing={0.95}>
            <Box>
              {sourceModel.imageUrl ? (
                <img
                  src={sourceModel.imageUrl}
                  alt={sourceModel.title || 'Original source'}
                  style={{ display: 'block', width: '100%', height: 'auto', borderRadius: 12, marginBottom: 8 }}
                />
              ) : null}
              <Button
                size="small"
                onClick={() => setOriginalOpen((current) => !current)}
                sx={{
                  px: 0,
                  color: colors.brand.deep,
                  fontWeight: 500,
                  textTransform: 'none',
                  borderRadius: '999px',
                }}
              >
                {sourceModel.imageUrl
                  ? (originalOpen ? 'Hide extracted text' : 'View extracted text')
                  : (originalOpen ? 'Hide original source' : 'View original source')}
              </Button>
            </Box>

            <Collapse in={originalOpen} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  p: 1,
                  borderRadius: '14px',
                  border: `1px solid ${alpha(colors.semantic.line, 0.9)}`,
                  bgcolor: alpha(colors.brand.tint, 0.04),
                }}
              >
                <Stack spacing={0.75}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: colors.semantic.ink, lineHeight: 1.25 }}>
                    Original source
                  </Typography>
                  {sourceLoading ? (
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <CircularProgress size={14} thickness={4} />
                      <Typography sx={{ fontSize: '0.82rem', color: alpha(colors.semantic.ink, 0.64), lineHeight: 1.35 }}>
                        Loading the saved original...
                      </Typography>
                    </Stack>
                  ) : null}
                  {sourceError ? (
                    <Alert severity="warning" sx={{ borderRadius: '12px', py: 0.4 }}>
                      {sourceError}
                    </Alert>
                  ) : null}
                  {originalTexts.length ? originalTexts.map((text, index) => (
                    <Typography
                      key={`${normalizeOriginalText(text).slice(0, 32)}-${index}`}
                      sx={{
                        fontSize: '0.84rem',
                        color: alpha(colors.semantic.ink, 0.72),
                        lineHeight: 1.45,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {text}
                    </Typography>
                  )) : (
                    <Typography sx={{ fontSize: '0.84rem', color: alpha(colors.semantic.ink, 0.62), lineHeight: 1.45 }}>
                      The original source is saved, but the full text is not available here yet.
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Collapse>

            {[
              ['Timeline', audit.timeline, (entry) => entry.title || entry.record?.title || entry.summary || entry.sourceText],
              ['Document details', audit.document, (group) => group.title || group.bucketLabel],
              ['Care Details', audit.details, (item) => item.title || item.content],
              ['Review', audit.review, (item) => item.title || item.name || item.summary || item.content],
              ['Profile updates', audit.profile, (item) => item.title || item.label || item.content],
            ].map(([label, entries, getLabel]) => entries.length ? (
              <Box key={label}>
                <Typography sx={{ mb: 0.6, fontSize: '0.8rem', fontWeight: 500, color: colors.brand.deep, letterSpacing: 0.6, textTransform: 'uppercase' }}>
                  {label}
                </Typography>
                <Stack spacing={0.7}>
                  {entries.map((entry, index) => label === 'Care Details' && entry.knowledgeType ? (
                    <KnowledgeItemCard key={entry.id || entry.clientId || index} item={entry} showSourceButton />
                  ) : label === 'Document details' ? (
                    <Box key={entry.id || index} sx={{ p: 0.9, borderRadius: '12px', bgcolor: alpha(colors.brand.tint, 0.06) }}>
                      <Typography sx={{ fontSize: '0.86rem', fontWeight: 750, color: colors.semantic.ink, lineHeight: 1.35 }}>
                        {entry.bucketLabel}: {entry.title}
                      </Typography>
                      <Stack spacing={0.4} sx={{ mt: 0.55 }}>
                        {entry.items.map((candidate, candidateIndex) => (
                          <Box key={candidate.id || `${entry.id}-${candidateIndex}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.65 }}>
                            <Typography component="span" aria-hidden="true" sx={{ mt: 0.05, fontSize: '0.93rem', fontWeight: 900, color: colors.semantic.sageDeep, lineHeight: 1.3 }}>
                              ✓
                            </Typography>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontSize: '0.84rem', color: colors.semantic.ink, lineHeight: 1.4 }}>
                                {candidate.title}
                              </Typography>
                              {candidate.text && candidate.text !== candidate.title ? (
                                <Typography sx={{ mt: 0.15, fontSize: '0.8rem', color: alpha(colors.semantic.ink, 0.68), lineHeight: 1.35 }}>
                                  {candidate.text}
                                </Typography>
                              ) : null}
                              {candidate.needsReview ? (
                                <Chip size="small" label="Needs review" sx={{ mt: 0.35, height: 20, fontSize: '0.68rem', fontWeight: 850, bgcolor: colors.semantic.profileFlow.careStory.warningBg, border: `1px solid ${colors.semantic.profileFlow.careStory.warningBorder}`, color: colors.semantic.profileFlow.careStory.warningText }} />
                              ) : null}
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  ) : (
                    <Box key={entry.id || entry.clientId || index} sx={{ p: 0.9, borderRadius: '12px', bgcolor: alpha(colors.brand.tint, 0.06) }}>
                      <Typography sx={{ fontSize: '0.86rem', color: colors.semantic.ink, lineHeight: 1.35 }}>
                        {getLabel(entry)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            ) : null)}
            {!audit.timeline.length && !audit.details.length && !audit.review.length && !audit.profile.length ? (
              <Typography sx={{ fontSize: '0.84rem', color: alpha(colors.semantic.ink, 0.62) }}>
                Nothing was extracted from this source.
              </Typography>
            ) : null}
          </Stack>
        </Collapse>
      </Stack>
    </Paper>
  );
};

const KnowledgeSubjectGroupCard = ({ group }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.1,
        borderRadius: '18px',
        border: `1px solid ${alpha(colors.brand.tint, 0.18)}`,
        bgcolor: '#FFFFFF',
        boxShadow: `0 10px 24px ${alpha(colors.semantic.ink, 0.05)}`,
      }}
    >
      <Stack spacing={0.95}>
        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '1.02rem', fontWeight: 500, color: colors.semantic.ink, lineHeight: 1.18 }}>
              {group.title} — {group.subtitle}
            </Typography>
          </Box>

          <IconButton
            aria-label={expanded ? 'Collapse subject' : 'Expand subject'}
            onClick={() => setExpanded((current) => !current)}
            sx={{
              flexShrink: 0,
              color: colors.brand.deep,
              width: 32,
              height: 32,
              bgcolor: alpha(colors.brand.tint, 0.1),
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 160ms ease, background-color 160ms ease',
              '&:hover': { bgcolor: alpha(colors.brand.tint, 0.18) },
            }}
          >
            <ExpandMoreRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Stack spacing={0.95}>
            <Stack spacing={0.7}>
              {group.items.map((item) => (
                <KnowledgeItemCard
                  key={item.id}
                  item={item}
                  showSourceButton
                />
              ))}
            </Stack>
          </Stack>
        </Collapse>
      </Stack>
    </Paper>
  );
};

const KnowledgeItemsSection = ({ childId = '', child = null }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(Boolean(childId));
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('subject');
  const {
    error: sourceHistoryError,
    loading: sourceHistoryLoading,
    sources,
  } = useCareSourceHistory({ childId });

  useEffect(() => {
    if (!childId) {
      setItems([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError('');
    return subscribeToKnowledgeItems({
      childId,
      onItems: (nextItems) => {
        setItems(nextItems);
        setLoading(false);
      },
      onError: () => {
        setError('Saved Care Knowledge could not load right now.');
        setLoading(false);
      },
    });
  }, [childId]);

  const groups = useMemo(() => groupKnowledgeItemsWithSources(items, sources), [items, sources]);
  const subjectGroups = useMemo(
    () => groupKnowledgeItemsByProfileChapter({ child: child || {}, items }),
    [child, items]
  );

  if (loading || (!items.length && sourceHistoryLoading)) {
    return (
      <Paper elevation={0} sx={{ p: 2, borderRadius: '16px', border: `1px solid ${alpha(colors.brand.tint, 0.18)}`, bgcolor: '#FFFFFF' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={20} thickness={4} />
          <Typography sx={{ fontSize: '0.92rem', color: alpha(colors.semantic.ink, 0.68) }}>
            Loading saved Care Knowledge...
          </Typography>
        </Stack>
      </Paper>
    );
  }

  if (error) {
    return <Alert severity="warning">{error}</Alert>;
  }

  if (!items.length && !sources.length) {
    return null;
  }

  return (
    <Box>
      <Stack spacing={1.15}>
        <Stack
          direction="row"
          spacing={2.6}
          alignItems="flex-end"
          sx={{
            borderBottom: `1px solid ${alpha(colors.semantic.line, 0.72)}`,
          }}
        >
          {[
            ['subject', 'By subject', items.length],
            ['source', 'By source', groups.length],
          ].map(([mode, label, count]) => {
            const selected = viewMode === mode;
            return (
              <Button
                key={mode}
                size="small"
                variant="text"
                onClick={() => setViewMode(mode)}
                sx={{
                  position: 'relative',
                  minWidth: 0,
                  minHeight: 34,
                  px: 0,
                  pb: 1,
                  borderRadius: 0,
                  color: selected ? colors.semantic.ink : alpha(colors.semantic.ink, 0.58),
                  fontSize: '0.9rem',
                  fontWeight: selected ? 600 : 500,
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: 'transparent',
                    color: colors.semantic.ink,
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: -1,
                    height: 3,
                    borderRadius: '999px 999px 0 0',
                    bgcolor: selected ? colors.brand.deep : 'transparent',
                  },
                }}
              >
                <Stack component="span" direction="row" spacing={0.75} alignItems="center">
                  <Box component="span">{label}</Box>
                  <Box
                    component="span"
                    sx={{
                      minWidth: 22,
                      height: 22,
                      px: 0.65,
                      borderRadius: '999px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: selected ? alpha(colors.brand.deep, 0.14) : alpha(colors.brand.tint, 0.12),
                      color: selected ? colors.brand.deep : alpha(colors.brand.deep, 0.78),
                      fontSize: '0.78rem',
                      fontWeight: 950,
                      lineHeight: 1,
                    }}
                  >
                    {count}
                  </Box>
                </Stack>
              </Button>
            );
          })}
        </Stack>

        {viewMode === 'subject'
          ? subjectGroups.map((group) => (
            <KnowledgeSubjectGroupCard key={group.id} group={group} />
          ))
          : sourceHistoryLoading
            ? <Typography>Loading source history...</Typography>
            : sourceHistoryError
              ? <Alert severity="warning">Source history could not load right now.</Alert>
              : groups.map((group) => (
                <KnowledgeSourceGroupCard key={group.id} group={group} />
              ))}
      </Stack>
    </Box>
  );
};

export default KnowledgeItemsSection;
